import { useCustomerOrderApiV1 } from '../../clients/customer-api/order/customer-order-api-v1';
import { CustomerOrder } from '../../clients/customer-api/order/customer-order';
import { Location } from '../../clients/quipli-service/location/location';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { OrderReturnStatus, OrderStatus } from '../../clients/quipli-service/order/order';
import './customer-order-list.scss';
import { useCustomerLocationApiV1 } from '../../clients/customer-api/location/customer-location-api-v1';
import { TenantContext } from '../../context/tenant-context';
import { Spinner } from '../../components/spinner/spinner';
import { EcommerceButton } from '../../components/form/button';
import _ from 'lodash';
import { displayMoneyFormat } from '../../util/util';
import { Interval } from 'luxon';
import classNames from 'classnames';
import { PaymentStatus } from '../../clients/quipli-service/payment/payment';
import { Card } from '../../components/card/card';
import { DateTimeCell } from '../../admin/order/order-table-cells';
import { PaymentMethodFormat, PaymentStatusBadge } from '../../admin/collectibles/invoice-list';
import { Typography } from '../../components/typography/typography';
import { Modal } from '../../components/modal/modal';
import { OrderRequestForm } from '../order-request/order-request-form';
import { InvoiceStatus, InvoiceTaxMeta, LineItem } from '../../clients/quipli-service/invoice/invoice';
import { StatusBadge, StatusBadgeType } from '../../components/status-badge/status-badge';
import { CustomerInvoice, CustomerPayment } from '../../clients/customer-api/invoice/customer-invoice';
import Icon from '../../components/icons/icon';
import StripePaymentAuthorization from '../payment/payment-authorization/payment-authorization';
import { ProductImageV2 } from '../../components/image/product/product-image-v2';
import { CustomerPaymentModal } from '../payment/components/customer-payment-modal';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { CustomerContext } from '../../context/customer-context';

export const ViewCustomerOrder = ({ orderId, payInvoice }: { orderId: number; payInvoice?: string }) => {
  const { contextPath } = useContext(TenantContext);
  const customer = useContext(CustomerContext)!;
  const { getOrderById } = useCustomerOrderApiV1();
  const { getLocationById } = useCustomerLocationApiV1();
  const { getConfigurations } = useCustomerAPI();
  const [order, setOrder] = useState<CustomerOrder>();
  const [location, setLocation] = useState<Location>();
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | undefined>();
  const [unauthenticatedPayment, setUnauthenticatedPayment] = useState<CustomerPayment>();
  const [modificationRequest, setModificationRequest] = useState<'extend' | 'call-off'>();
  const [paymentModal, setPaymentModal] = useState<ReactNode | undefined>(undefined);
  const [paymentAcknowledged, setPaymentAcknowledged] = useState<boolean>(false);
  const [portalPaymentEnabled, setPortalPaymentEnabled] = useState<boolean>(false);
  const [displayTaxByJurisdiction, setDisplayTaxByJurisdiction] = useState<boolean>(false);
  const [taxInclusive, setTaxInclusive] = useState<boolean>(false);
  const [taxLabel, setTaxLabel] = useState<string | undefined>(undefined);

  function CallOffExtendOrder(id: number, action: 'extend' | 'call-off') {
    // @ts-expect-error ajax
    callAjax(
      // @ts-expect-error ajax
      generateUrl('account', 'order_extend_form', '', ''),
      'mode=ajax&order_id=' + id + `&request_type=${action == 'extend' ? 1 : 2}`,
      // @ts-expect-error ajax
      function (t) {
        $('.modelHolder').html(t);
        // @ts-expect-error ajax
        $('#extendOrder').modal('show');
      },
    );
    return false;
  }

  function ReOrder(id: number) {
    // @ts-expect-error ajax
    callAjax(
      // @ts-expect-error ajax
      generateUrl('account', 'reorder_date_select_form', '', ''),
      `mode=ajax&order_id=${id}`,
      // @ts-expect-error ajax
      function (t) {
        $('.modelHolder').html(t);
        // @ts-expect-error ajax
        $('#reOrder').modal('show');
      },
    );
    return false;
  }

  async function orderInit() {
    if (order) {
      order.invoices.length === 1 && setExpandedInvoiceId(order.invoices[0].id);
      const _location = await getLocationById(order.locationId);
      setLocation(_location);
      if (payInvoice && !paymentAcknowledged && portalPaymentEnabled) {
        const invoice = order?.invoices.find((i) => i.id === payInvoice);
        if (invoice && invoice.balance > 0) {
          setPaymentModal(
            <CustomerPaymentModal
              isOpen={true}
              close={async () => {
                setPaymentModal(undefined);
                setPaymentAcknowledged(true);
                await load();
              }}
              currency={_location.currency}
              invoice={invoice}
            />,
          );
        }
      }
    }
  }

  useEffect(() => {
    orderInit();
  }, [order]);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    const order = await getOrderById(orderId);
    setOrder(order);
    const configs = await getConfigurations([
      'CONF_CUSTOMER_PORTAL_PAYMENT',
      'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN',
      'CONF_SHOW_PRICES_WITH_TAX',
      'CONF_PRICE_TAX_LABEL',
    ]);
    const customerPortalPayment = configs.find((c) => c.variable === 'CONF_CUSTOMER_PORTAL_PAYMENT')?.value === '1';
    setPortalPaymentEnabled(customerPortalPayment);
    const displayTaxByJurisdiction =
      configs.find((c) => c.variable === 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN')?.value === '1';
    setDisplayTaxByJurisdiction(displayTaxByJurisdiction);
    const showTaxInclusive = configs.find((c) => c.variable === 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1';
    setTaxInclusive(showTaxInclusive);
    const priceTaxLabel = configs.find((c) => c.variable === 'CONF_PRICE_TAX_LABEL')?.value;
    setTaxLabel(priceTaxLabel);
  }

  const onclickMyAccount = () => {
    window.open(`${contextPath}account/my_orders`, '_self');
  };

  function getTaxPricing(lineItem: LineItem) {
    if (lineItem.taxable && customer.chargeTax) {
      const taxRate =
        ((lineItem.meta?.tax as InvoiceTaxMeta[])?.reduce((a, b) => {
          return a + b.percentage;
        }, 0) ?? 0) / 100;
      const totalTax =
        (lineItem.meta?.tax as InvoiceTaxMeta[])?.reduce((a, b) => {
          return a + b.amount;
        }, 0) ?? 0;
      return (
        <>
          <td className="text-center">
            {displayMoneyFormat(lineItem.unitPrice * (1 + taxRate), location?.currency)} incl. {taxLabel}
          </td>
          <td className="text-center">{lineItem.quantity}</td>
          <td className="text-right">
            {displayMoneyFormat(lineItem.total + totalTax, location?.currency)} incl. {taxLabel}
          </td>
        </>
      );
    }
    return (
      <>
        <td className="text-center">
          {displayMoneyFormat(lineItem.unitPrice, location?.currency)} no {taxLabel} incl.
        </td>
        <td className="text-center">{lineItem.quantity}</td>
        <td className="text-right">
          {displayMoneyFormat(lineItem.total, location?.currency)} no {taxLabel} incl.
        </td>
      </>
    );
  }

  return (
    <div className="view-customer-order mt-4">
      {order ? (
        <div className="container flex flex-column gap-3">
          <div className="flex flex-column justify-items-center">
            <div className="flex flex-wrap w-full items-start gap-2">
              <EcommerceButton className={'fa fa-arrow-left primary'} onClick={onclickMyAccount}></EcommerceButton>
              <Typography type={'display-sm'}>
                {order.status === OrderStatus.Quote ? 'Quote' : 'Order'} details for{' '}
                {order.status === OrderStatus.Quote ? 'quote' : 'order'} {order.orderNumber}
              </Typography>
            </div>
            <div className={'flex flex-column'}>
              <Typography type={'text-md'} className="text-grey mr-16">
                Order date {renderOrderDate(order)}
              </Typography>
            </div>
            <div className={'order-actions flex flex-wrap gap-2 justify-end'}>
              {order.status === OrderStatus.Approved && !order.isCycleBilled && (
                <>
                  {[OrderReturnStatus.NotStarted, OrderReturnStatus.OnRent].includes(order.returnStatus) && (
                    <EcommerceButton
                      className="main-btn main-btn--sm callOff"
                      onClick={() => CallOffExtendOrder(order.id, 'call-off')}
                    >
                      Call-Off
                    </EcommerceButton>
                  )}
                  <EcommerceButton
                    className="main-btn main-btn--sm extend"
                    onClick={() => CallOffExtendOrder(order.id, 'extend')}
                  >
                    Extend
                  </EcommerceButton>
                </>
              )}
              {order.returnStatus === OrderReturnStatus.Complete && (
                <EcommerceButton className="main-btn main-btn--sm callOff" onClick={() => ReOrder(order.id)}>
                  Re-Order
                </EcommerceButton>
              )}
            </div>
          </div>

          <table className={'gap-3 w-full'}>
            <thead>
              <tr>
                <th>Product</th>
                <th>Rental Duration</th>
                <th>Unit Price</th>
                <th>Qty</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {order.products.map((op) => {
                return (
                  <tr>
                    <td className="text-left flex flex-col gap-2 items-center p-2">
                      <ProductImageV2 productId={op.product.id} profile="THUMB" className={'p-2'} />
                      <div className={'p-2 '}>
                        <span>{op.product.name}</span>
                        {op.additionalFees.map((pf) => {
                          const feeCost = _.sum(
                            order.invoices
                              .flatMap((inv) => inv.lineItems)
                              .filter((li) => li.type === 'ProductFee' && li.typeId === pf.id.toString())
                              .map((li) => li.total),
                          );
                          return (
                            <div>
                              <span>
                                {pf.feeTitle}: {displayMoneyFormat(feeCost, location?.currency)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="text-left p-2">
                      {op.rentalEndDate && (
                        <div className={'flex flex-col'}>
                          Duration:{' '}
                          {Interval.fromDateTimes(op.rentalStartDate, op.rentalEndDate)
                            .toDuration()
                            .rescale()
                            .toHuman()}
                        </div>
                      )}
                      <div>From: {op.rentalStartDate.toFormat('D t')}</div>
                      <div>To: {op.rentalEndDate?.toFormat('D t') || '-'}</div>
                    </td>
                    <td className={'p-2'}>
                      {displayMoneyFormat(
                        _.sum(
                          order.invoices
                            .flatMap((inv) => inv.lineItems)
                            .filter((li) => li.type === 'OrderProduct' && li.typeId === op.id.toString())
                            .map((li) => li.unitPrice),
                        ),
                        location?.currency,
                      )}
                    </td>
                    <td className={'p-2'}>
                      {_.sum(
                        order.invoices
                          .flatMap((inv) => inv.lineItems)
                          .filter((li) => li.type === 'OrderProduct' && li.typeId === op.id.toString())
                          .map((li) => li.quantity),
                      )}
                    </td>
                    <td className={'p-2'}>
                      {displayMoneyFormat(
                        _.sum(
                          order.invoices
                            .flatMap((inv) => inv.lineItems)
                            .filter((li) => li.type === 'OrderProduct' && li.typeId === op.id.toString())
                            .map((li) => li.total),
                        ),
                        location?.currency,
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="invoice-table">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="inline md:hidden"></th>
                  <th className="text-left">Invoice No.</th>
                  <th>Due Date</th>
                  <th>Date</th>
                  <th className="text-right">Tax</th>
                  <th className="text-right">Total</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Payments</th>
                  <th className="text-right">Balance</th>
                  <th className="text-center"></th>
                </tr>
              </thead>
              <tbody>
                {order.invoices.map((invoice) => {
                  const totalPayments = _.sum(
                    invoice.payments
                      .filter((p) => p.status === PaymentStatus.succeeded && p.amount > 0)
                      .flatMap((p) => p.invoicePayments.map((_) => _.amount)),
                  );
                  const totalRefunds = _.sum(
                    invoice.payments
                      .filter((p) => p.status === PaymentStatus.succeeded && p.amount < 0)
                      .flatMap((p) => p.invoicePayments.map((_) => _.amount)),
                  );
                  const netPayments = _.sum(
                    invoice.payments
                      .filter((p) => p.status === PaymentStatus.succeeded)
                      .flatMap((p) => p.invoicePayments.map((_) => _.amount)),
                  );
                  return (
                    <React.Fragment key={invoice.id}>
                      <tr
                        className={classNames('invoice-row', { expanded: expandedInvoiceId === invoice.id })}
                        onClick={() =>
                          setExpandedInvoiceId((invoiceId) => (invoiceId === invoice.id ? undefined : invoice.id))
                        }
                      >
                        <td className="inline md:hidden">
                          <InvoiceActions
                            order={order}
                            invoice={invoice}
                            location={location}
                            portalPaymentEnabled={portalPaymentEnabled}
                            customerPaymentBan={customer.paymentBan}
                            setPaymentModal={setPaymentModal}
                            refresh={load}
                          />
                        </td>
                        <td className="text-left">
                          <div className="flex gap-2 items-center mx-1">
                            <span>{invoice.invoiceNumber}</span>
                            <i
                              className={classNames('fa text-[var(--grey-500)]', {
                                'fa-angle-up': expandedInvoiceId === invoice.id,
                                'fa-angle-down': expandedInvoiceId !== invoice.id,
                              })}
                            />
                          </div>
                        </td>
                        <td className="text-left">{invoice.dueDate?.toFormat('D')}</td>
                        <td className="text-left">{invoice.date?.toFormat('D')}</td>
                        <td className="text-right">{displayMoneyFormat(invoice.tax, location?.currency)}</td>
                        <td className="text-right">{displayMoneyFormat(invoice.total, location?.currency)}</td>
                        <td className="text-center">{InvoiceStatusBadge(invoice)}</td>
                        <td className="text-right">
                          {netPayments !== 0 ? displayMoneyFormat(netPayments, location?.currency) : ''}
                        </td>
                        <td className="text-right">
                          {invoice.balance !== 0 ? displayMoneyFormat(invoice.balance, location?.currency) : ''}
                        </td>
                        <td className="md:!inline" style={{ display: 'none' }}>
                          <InvoiceActions
                            order={order}
                            invoice={invoice}
                            location={location}
                            portalPaymentEnabled={portalPaymentEnabled}
                            customerPaymentBan={customer.paymentBan}
                            setPaymentModal={setPaymentModal}
                            refresh={load}
                          />
                        </td>
                      </tr>
                      {invoice.id === expandedInvoiceId && (
                        <tr className={classNames('invoice-detail', { expanded: expandedInvoiceId === invoice.id })}>
                          <td colSpan={100}>
                            <div className="p-2 pt-0 flex flex-column gap-2">
                              <Card className="w-full">
                                <h6 className="p-2">Invoice Details</h6>
                                <table className="w-full">
                                  <thead>
                                    <tr>
                                      <th className="text-left">Name</th>
                                      <th className="text-left">Description</th>
                                      <th className="text-center">Unit Price</th>
                                      <th className="text-center">Quantity</th>
                                      <th className="text-right">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoice.lineItems
                                      .filter((_) => _.type !== 'Tax')
                                      .map((li) => (
                                        <tr key={li.id}>
                                          <td className="text-left">{li.name}</td>
                                          <td className="text-left">
                                            {li.description?.split('\n').map((line, i) => (
                                              <div key={i}>{line}</div>
                                            ))}
                                          </td>
                                          {taxInclusive ? (
                                            <>{getTaxPricing(li)}</>
                                          ) : (
                                            <>
                                              <td className="text-center">
                                                {displayMoneyFormat(li.unitPrice, location?.currency)}
                                              </td>
                                              <td className="text-center">{li.quantity}</td>
                                              <td className="text-right">
                                                {displayMoneyFormat(li.total, location?.currency)}
                                              </td>
                                            </>
                                          )}
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </Card>
                              <div className="flex gap-2">
                                <Card className="grow">
                                  <h6>Payment History</h6>

                                  <table className="w-full">
                                    <thead>
                                      <tr>
                                        <th className="text-left">Method</th>
                                        <th className="text-left">Date</th>
                                        <th className="text-left">Status</th>
                                        <th className="text-right">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {invoice.payments.length > 0 ? (
                                        invoice.payments.map((p) => {
                                          return (
                                            <tr key={p.id}>
                                              <td className="text-left">
                                                {PaymentMethodFormat(p.method, p.methodDetail)}
                                              </td>
                                              <td className="text-left">
                                                <DateTimeCell date={p.paidAt} />
                                              </td>
                                              <td className="text-left">
                                                <div className={'flex items-center gap-2'}>
                                                  {PaymentStatusBadge(p)}
                                                  {p.gatewayStatus == 'requires_action' && (
                                                    <EcommerceButton
                                                      className="button button-primary authorize"
                                                      onClick={() => setUnauthenticatedPayment(p)}
                                                    >
                                                      Confirm Payment
                                                    </EcommerceButton>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="text-right">
                                                {displayMoneyFormat(p.amount, location?.currency)}
                                              </td>
                                            </tr>
                                          );
                                        })
                                      ) : (
                                        <tr>
                                          <td className="text-center text-[var(--grey-500)]" colSpan={100}>
                                            None
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </Card>
                                <Card className="p-4 min-w-[20%] totals-card">
                                  <ul className="list-none p-0 flex flex-col gap-2">
                                    <li className="flex gap-12 justify-between">
                                      <span>Subtotal</span>
                                      <span className="text-right">
                                        {displayMoneyFormat(invoice.subtotal, location?.currency)}
                                      </span>
                                    </li>
                                    {displayTaxByJurisdiction ? (
                                      invoice.lineItems
                                        .filter((li) => li.type === 'Tax' && li.total !== 0)
                                        .map((li) => {
                                          let description = `Tax ${li.description ?? li.name}`;
                                          if (li.description) {
                                            const match = li.description.match(/^Jurisdiction: (.*)Rate: (.*)$/);
                                            if (match) {
                                              description = `${match[1]} (${match[2]})`;
                                            }
                                          }
                                          return (
                                            <li className="flex gap-4 justify-between grey">
                                              <span>{description}</span>
                                              <span className="text-right">
                                                {displayMoneyFormat(li.total, location?.currency)}
                                              </span>
                                            </li>
                                          );
                                        })
                                    ) : (
                                      <li className="flex gap-4 justify-between grey">
                                        <span>
                                          Tax ({(invoice.taxRate * 100).toFixed(2).replace(/[.,]00$/, '')}
                                          %)
                                        </span>
                                        <span className="text-right">
                                          {displayMoneyFormat(invoice.tax, location?.currency)}
                                        </span>
                                      </li>
                                    )}
                                    <li className="flex gap-4 justify-between bold">
                                      <span>Total</span>
                                      <span className="text-right">
                                        {displayMoneyFormat(invoice.total, location?.currency)}
                                      </span>
                                    </li>
                                    {totalPayments !== 0 && (
                                      <li className="flex gap-4 justify-between grey">
                                        <span>Payment(s)</span>
                                        <span className="text-right">
                                          {displayMoneyFormat(totalPayments, location?.currency)}
                                        </span>
                                      </li>
                                    )}
                                    {totalRefunds !== 0 && (
                                      <li className="flex gap-4 justify-between grey">
                                        <span>Refund(s)</span>
                                        <span className="text-right">
                                          {displayMoneyFormat(totalRefunds, location?.currency)}
                                        </span>
                                      </li>
                                    )}
                                    <li className="flex gap-4 justify-between bold">
                                      <span>Balance Due</span>
                                      <span className="text-right">
                                        {displayMoneyFormat(invoice.balance, location?.currency)}
                                      </span>
                                    </li>
                                  </ul>
                                </Card>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <Spinner />
      )}
      {order && unauthenticatedPayment && (
        <StripePaymentAuthorization orderId={order.id} payment={unauthenticatedPayment} />
      )}
      {order && (
        <Modal
          title={modificationRequest == 'call-off' ? 'Call-Off Order' : 'Extend Order'}
          isOpen={!!modificationRequest}
          close={() => setModificationRequest(undefined)}
        >
          <OrderRequestForm orderId={order.id} requestType={modificationRequest!} />
        </Modal>
      )}
      {paymentModal}
    </div>
  );
};

const InvoiceActions = ({
  order,
  invoice,
  location,
  portalPaymentEnabled,
  customerPaymentBan,
  setPaymentModal,
  refresh,
}: {
  order: CustomerOrder;
  invoice: CustomerInvoice;
  location?: Location;
  portalPaymentEnabled: boolean;
  customerPaymentBan: boolean;
  setPaymentModal: (modal: ReactNode) => void;
  refresh: () => Promise<void>;
}) => {
  const { contextPath } = useContext(TenantContext);

  const onDownloadClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    window.open(
      invoice.status == InvoiceStatus.Draft
        ? order?.signature && order.signature !== ''
          ? `${contextPath}legacy-app/order/contract_rental_policy/${order?.id}/signed`
          : `${contextPath}legacy-app/order/contract_rental_policy/${order?.id}/unsigned`
        : `${contextPath}quipli-service/customer-api/v1/invoices/${invoice.typeId}/invoices/${invoice.id}`,
    );
  };

  const onPayClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setPaymentModal(
      <CustomerPaymentModal
        isOpen={true}
        close={async () => {
          setPaymentModal(undefined);
          await refresh();
        }}
        currency={location!.currency}
        invoice={invoice}
      />,
    );
  };
  return (
    <div className="flex flex-row justify-between gap-1 px-3  shrink-0 ">
      <EcommerceButton className="button button-primary shrink-0" onClick={onDownloadClick}>
        <Icon icon="Download" stroke="white" />
        <span className="md:!inline" style={{ display: 'none' }}>
          Download
        </span>
      </EcommerceButton>
      {location &&
        invoice.balance > 0 &&
        invoice.status == InvoiceStatus.Approved &&
        portalPaymentEnabled &&
        !customerPaymentBan && (
          <EcommerceButton buttonStyle="primary" className="shrink-0" onClick={onPayClick}>
            <span className="inline md:hidden">Pay</span>
            <span className="md:!inline" style={{ display: 'none' }}>
              Pay Invoice
            </span>
          </EcommerceButton>
        )}
    </div>
  );
};

function renderOrderDate(order: CustomerOrder) {
  if (order.status === OrderStatus.Quote) {
    if (order.quoteExpiresAt) {
      if (order.quoteExpiresAt.diffNow().milliseconds < 0) {
        return <span className="quote-expired">Expired ${order.quoteExpiresAt.toFormat('D t')}</span>;
      } else {
        return <span>Expires ${order.quoteExpiresAt.toFormat('D t')}</span>;
      }
    } else {
      return undefined;
    }
  } else {
    return order.dateAdded.toFormat('D');
  }
}
export const InvoiceStatusBadge = (invoice: CustomerInvoice) => {
  let status, statusType: StatusBadgeType;
  switch (invoice.status) {
    case InvoiceStatus.Void:
      status = 'Void';
      statusType = 'red';
      break;
    case InvoiceStatus.Draft:
      status = 'Draft';
      statusType = 'grey';
      break;
    case InvoiceStatus.Approved:
      if (invoice.balance === 0) {
        status = 'Paid';
        statusType = 'green';
      } else if (invoice.balance > 0) {
        if (invoice.dueDate && invoice.dueDate.endOf('day').diffNow().milliseconds <= 0) {
          status = 'Past Due';
          statusType = 'red';
        } else {
          status = 'Payable';
          statusType = 'grey';
        }
      } else {
        status = 'Pending Refund';
        statusType = 'yellow';
      }
      break;
  }

  return <StatusBadge label={status} type={statusType} />;
};
